import type { CSSProperties } from 'react';
import type { Branding } from './types';

/**
 * A sitter's colours, mapped onto the design tokens.
 *
 * Only the brand accent is theirs to set. The state colours stay fixed, because
 * green means current and red means expired everywhere in the product, and a
 * business whose brand happens to be red must not end up with a storefront that
 * reads as one long warning.
 */

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * The paper the app is printed on, from @theme in index.css.
 *
 * This has to track --color-sheet by hand, and it silently drifted once
 * already: the tint below was being mixed onto a sheet colour the product had
 * stopped using, so a sitter's pale tint sat a shade off her own page.
 */
const SHEET = { r: 0xfd, g: 0xf8, b: 0xf5 };

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parse(hex: string): Rgb | null {
  if (!HEX.test(hex)) return null;
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

const toHex = ({ r, g, b }: Rgb): string =>
  `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;

/** WCAG relative luminance, 0 for black and 1 for white. */
function luminance({ r, g, b }: Rgb): number {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

const mix = (a: Rgb, b: Rgb, weight: number): Rgb => ({
  r: a.r * (1 - weight) + b.r * weight,
  g: a.g * (1 - weight) + b.g * weight,
  b: a.b * (1 - weight) + b.b * weight,
});

/**
 * The brand colour is used behind sheet-coloured text, so a pale one would
 * leave a button with a white label on a nearly white ground. Darken until it
 * carries light text, rather than refusing the colour the sitter chose.
 */
function legibleOnLightText(rgb: Rgb): Rgb {
  let out = rgb;
  let guard = 0;
  while (luminance(out) > 0.35 && guard < 12) {
    out = mix(out, { r: 0, g: 0, b: 0 }, 0.12);
    guard += 1;
  }
  return out;
}

/**
 * Inline custom properties for a branded subtree. Returns nothing when the
 * business has no colours set, so the platform's own palette shows through.
 *
 * The values come from a public API payload, so they are re-validated here:
 * only a six-digit hex ever reaches a style property.
 */
export function brandStyle(branding?: Branding): CSSProperties {
  if (!branding) return {};
  const style: Record<string, string> = {};

  const primary = parse(branding.primaryColor);
  if (primary) {
    const ink = legibleOnLightText(primary);
    style['--color-stamp'] = toHex(ink);
    // The tint behind selected rows and pending stamps: the same hue, laid on
    // the paper rather than an unrelated colour.
    style['--color-stamp-soft'] = toHex(mix(SHEET, ink, 0.13));
    // The pressed and hovered state of every primary button, and the ink of a
    // pending stamp. Leaving it underived was the visible half of the same
    // drift: a rose-branded business got a rose button that turned the
    // platform's violet under the cursor.
    style['--color-stamp-deep'] = toHex(mix(ink, { r: 0, g: 0, b: 0 }, 0.18));
  }

  const accent = parse(branding.accentColor);
  if (accent) style['--color-brand-accent'] = toHex(accent);

  return style as CSSProperties;
}
